<script lang="ts">
  import { router } from '@inertiajs/svelte';
  import { onMount, onDestroy } from 'svelte';

  interface Reply {
    id: string;
    threads_reply_id: string;
    author_username: string;
    author_display_name: string | null;
    author_avatar_url: string | null;
    content: string;
    likes_count: number;
    depth: number;
    replies_count: number;
    parent_reply_id: string | null;
    created_at: string;
    posted_at: string | null;
    threads_url: string | null;
    dm_prepared_at: string | null;
    threads_post_id: string;
    post_content_preview: string;
  }

  interface PostOption {
    id: string;
    threads_id: string;
    content_preview: string;
    posted_at: string | null;
  }

  interface Pagination {
    current_page: number;
    total_pages: number;
    total_count: number;
    per_page: number;
  }

  interface Stats {
    total: number;
    unique_authors: number;
    last_24h: number;
  }

  let {
    replies = [],
    posts = [],
    pagination = {} as Pagination,
    filters = {},
    stats = {} as Stats
  } = $props<{
    replies: Reply[];
    posts: PostOption[];
    pagination: Pagination;
    filters: { threads_post_id?: string; author_username?: string; search?: string; has_replies?: string; dm_status?: string };
    stats: Stats;
  }>();

  // svelte-ignore state_referenced_locally
  let selectedPostId = $state(filters.threads_post_id ?? '');
  // svelte-ignore state_referenced_locally
  let searchTerm = $state(filters.search ?? '');
  // svelte-ignore state_referenced_locally
  let authorTerm = $state(filters.author_username ?? '');
  // svelte-ignore state_referenced_locally
  let hasRepliesFilter = $state(filters.has_replies ?? '');
  // svelte-ignore state_referenced_locally
  let dmStatusFilter = $state(filters.dm_status ?? '');

  let showFilters = $state(false);

  // Accumulated replies for infinite scroll
  // svelte-ignore state_referenced_locally
  let allReplies = $state<Reply[]>([...replies]);
  // svelte-ignore state_referenced_locally
  let currentPage = $state(pagination.current_page ?? 1);
  // svelte-ignore state_referenced_locally
  let totalPages = $state(pagination.total_pages ?? 1);
  let loadingMore = $state(false);

  let expanded: Record<string, Reply[] | 'loading' | 'error'> = $state({});
  let dmPrepared: Record<string, string | null> = $state({});

  const SURVEY_URL = 'https://www.axhub.space/vibe_study/survey/vibestudy-pilot-1';
  const DM_TEMPLATE = (username: string) =>
    `@${username}님 안녕하세요! 여의도 AI 스터디 "AI Do!" 모집글에 관심 주셔서 감사합니다 🙌\n` +
    `5분이면 끝나는 설문 응답 부탁드려요. 주신 내용 바탕으로 이번 주 내 (늦어도 4월 중) 안내드릴게요:\n${SURVEY_URL}`;

  function buildParams(page?: number): URLSearchParams {
    const p = new URLSearchParams();
    if (selectedPostId)   p.set('threads_post_id', selectedPostId);
    if (searchTerm)       p.set('search', searchTerm);
    if (authorTerm)       p.set('author_username', authorTerm.replace(/^@/, ''));
    if (hasRepliesFilter) p.set('has_replies', hasRepliesFilter);
    if (dmStatusFilter)   p.set('dm_status', dmStatusFilter);
    if (page)             p.set('page', String(page));
    return p;
  }

  function applyFilter() {
    const params = buildParams(1);
    router.get('/admin/community/comments', Object.fromEntries(params), {
      preserveState: false, replace: true
    });
  }

  function clearFilters() {
    selectedPostId = ''; searchTerm = ''; authorTerm = '';
    hasRepliesFilter = ''; dmStatusFilter = '';
    router.get('/admin/community/comments');
  }

  async function loadMore() {
    if (loadingMore || currentPage >= totalPages) return;
    loadingMore = true;
    try {
      const params = buildParams(currentPage + 1);
      const res = await fetch(`/admin/community/comments?${params}`, {
        headers: {
          'X-Inertia': 'true',
          'X-Inertia-Version':
            (document.querySelector('meta[name="inertia-version"]') as HTMLMetaElement | null)?.content ?? '',
          Accept: 'text/html, application/xhtml+xml'
        }
      });
      const data = await res.json();
      const newReplies = data?.props?.replies ?? [];
      allReplies = [...allReplies, ...newReplies];
      currentPage = data?.props?.pagination?.current_page ?? currentPage + 1;
      totalPages  = data?.props?.pagination?.total_pages  ?? totalPages;
    } catch (_e) {
      /* noop */
    } finally {
      loadingMore = false;
    }
  }

  // Infinite-scroll sentinel
  let sentinel: HTMLDivElement | null = $state(null);
  let observer: IntersectionObserver | null = null;

  onMount(() => {
    observer = new IntersectionObserver((entries) => {
      if (entries[0]?.isIntersecting) loadMore();
    }, { rootMargin: '200px' });
  });

  $effect(() => {
    if (!observer) return;
    observer.disconnect();
    if (sentinel) observer.observe(sentinel);
  });

  onDestroy(() => observer?.disconnect());

  async function markDm(r: Reply, mode: 'mark' | 'unmark') {
    try {
      const csrf = document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content ?? '';
      const res = await fetch(`/admin/community/comments/${r.id}/mark_dm_prepared`, {
        method: 'POST',
        headers: { 'X-CSRF-Token': csrf, Accept: 'application/json', 'Content-Type': 'application/json' },
        body: JSON.stringify({ action_mode: mode })
      });
      if (res.ok) {
        const data = await res.json();
        dmPrepared[r.id] = data.dm_prepared_at ?? null;
        dmPrepared = { ...dmPrepared };
      }
    } catch (_e) { /* noop */ }
  }

  async function prepareDm(r: Reply) {
    const msg = DM_TEMPLATE(r.author_username);
    try {
      await navigator.clipboard.writeText(msg);
    } catch (_e) {
      alert('클립보드 복사 실패. 메시지를 수동 복사하세요:\n\n' + msg);
    }
    window.open(`https://www.threads.net/@${r.author_username}`, '_blank', 'noopener');
    await markDm(r, 'mark');
  }

  async function undoDm(r: Reply) {
    if (!confirm(`@${r.author_username} DM 전송 표시를 취소하시겠어요?`)) return;
    await markDm(r, 'unmark');
  }

  function isDmPrepared(r: Reply): boolean {
    if (r.id in dmPrepared) return !!dmPrepared[r.id];
    return !!r.dm_prepared_at;
  }

  function triggerRefetch() {
    if (!selectedPostId) { alert('게시물을 먼저 선택하세요'); return; }
    router.post('/admin/community/comments/refetch', { threads_post_id: selectedPostId });
  }

  function exportCsv() {
    const params = buildParams();
    window.location.href = `/admin/community/comments/export_csv?${params}`;
  }

  async function toggleChildren(id: string) {
    if (id in expanded) {
      delete expanded[id];
      expanded = { ...expanded };
      return;
    }
    expanded[id] = 'loading';
    expanded = { ...expanded };
    try {
      const r = await fetch(`/admin/community/comments/${id}/children`, {
        headers: { Accept: 'application/json' }
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const data = await r.json();
      expanded[id] = data.replies as Reply[];
      expanded = { ...expanded };
    } catch (_e) {
      expanded[id] = 'error';
      expanded = { ...expanded };
    }
  }

  let activeFilterCount = $derived(
    [selectedPostId, searchTerm, authorTerm, hasRepliesFilter, dmStatusFilter]
      .filter(Boolean).length
  );
</script>

<div class="space-y-6 p-6">
  <header class="flex items-start justify-between gap-3 flex-wrap">
    <div>
      <h1 class="text-2xl font-bold">VibeStudy — 댓글 관리</h1>
      <p class="text-sm text-gray-500 mt-1">
        전체 {allReplies.length} / {pagination.total_count ?? 0}건 로드됨
      </p>
    </div>
    <div class="flex gap-2 items-start">
      <button onclick={() => showFilters = !showFilters}
              class="px-3 py-2 rounded border text-sm font-medium whitespace-nowrap
                     {showFilters ? 'bg-gray-900 text-white' : 'bg-white hover:bg-gray-100'}">
        🔍 필터{activeFilterCount > 0 ? ` (${activeFilterCount})` : ''}
      </button>
      <button onclick={triggerRefetch}
              class="px-3 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 text-sm whitespace-nowrap">
        🔄 재수집
      </button>
      <button onclick={exportCsv}
              class="px-3 py-2 bg-gray-200 rounded hover:bg-gray-300 text-sm whitespace-nowrap">
        📥 CSV
      </button>
    </div>
  </header>

  <!-- Stats -->
  <div class="grid grid-cols-3 gap-4">
    <div class="p-4 bg-white border rounded">
      <div class="text-sm text-gray-500">전체 댓글</div>
      <div class="text-2xl font-bold">{stats.total}</div>
    </div>
    <div class="p-4 bg-white border rounded">
      <div class="text-sm text-gray-500">고유 작성자</div>
      <div class="text-2xl font-bold">{stats.unique_authors}</div>
    </div>
    <div class="p-4 bg-white border rounded">
      <div class="text-sm text-gray-500">최근 24h</div>
      <div class="text-2xl font-bold">{stats.last_24h}</div>
    </div>
  </div>

  <!-- Filter panel (right-aligned, collapsible) -->
  {#if showFilters}
    <div class="bg-white border rounded-lg p-4 space-y-3 ml-auto max-w-2xl shadow-sm">
      <div class="flex items-center justify-between">
        <h3 class="font-semibold text-sm">필터</h3>
        {#if activeFilterCount > 0}
          <button onclick={clearFilters} class="text-xs text-gray-500 hover:underline">
            전체 초기화
          </button>
        {/if}
      </div>

      <div class="grid grid-cols-2 gap-3">
        <div class="col-span-2">
          <label for="author-input" class="block text-xs font-medium text-gray-700 mb-1">
            작성자 아이디
          </label>
          <input id="author-input" type="text" bind:value={authorTerm}
                 onkeydown={(e) => e.key === 'Enter' && applyFilter()}
                 class="w-full border rounded px-3 py-2 text-sm font-mono"
                 placeholder="@username 또는 username" />
        </div>

        <div class="col-span-2">
          <label for="search-input" class="block text-xs font-medium text-gray-700 mb-1">
            본문 검색
          </label>
          <input id="search-input" type="text" bind:value={searchTerm}
                 onkeydown={(e) => e.key === 'Enter' && applyFilter()}
                 class="w-full border rounded px-3 py-2 text-sm"
                 placeholder="키워드…" />
        </div>

        <div class="col-span-2">
          <label for="post-filter" class="block text-xs font-medium text-gray-700 mb-1">
            게시물
          </label>
          <select id="post-filter" bind:value={selectedPostId}
                  class="w-full border rounded px-3 py-2 text-sm">
            <option value="">전체</option>
            {#each posts as p}
              <option value={p.id}>{p.content_preview} ({p.threads_id})</option>
            {/each}
          </select>
        </div>

        <div>
          <label for="has-replies-filter" class="block text-xs font-medium text-gray-700 mb-1">
            답글 여부
          </label>
          <select id="has-replies-filter" bind:value={hasRepliesFilter}
                  class="w-full border rounded px-3 py-2 text-sm">
            <option value="">전체</option>
            <option value="yes">답글 있음</option>
            <option value="no">답글 없음</option>
          </select>
        </div>

        <div>
          <label for="dm-status-filter" class="block text-xs font-medium text-gray-700 mb-1">
            DM 상태
          </label>
          <select id="dm-status-filter" bind:value={dmStatusFilter}
                  class="w-full border rounded px-3 py-2 text-sm">
            <option value="">전체</option>
            <option value="sent">전송됨</option>
            <option value="unsent">미전송</option>
          </select>
        </div>
      </div>

      <div class="flex justify-end gap-2 pt-2 border-t">
        <button onclick={applyFilter}
                class="px-4 py-2 bg-gray-900 text-white rounded text-sm">
          검색 적용
        </button>
      </div>
    </div>
  {/if}

  <!-- Table -->
  <div class="bg-white border rounded overflow-hidden">
    <table class="w-full">
      <thead class="bg-gray-50">
        <tr class="text-left text-sm">
          <th class="p-3 w-8"></th>
          <th class="p-3">작성자</th>
          <th class="p-3">본문</th>
          <th class="p-3">답글</th>
          <th class="p-3">작성</th>
          <th class="p-3"></th>
        </tr>
      </thead>
      <tbody>
        {#each allReplies as r (r.id)}
          <tr class="border-t hover:bg-gray-50">
            <td class="p-3 align-top">
              {#if r.replies_count > 0}
                <button onclick={() => toggleChildren(r.id)}
                        class="w-6 h-6 flex items-center justify-center rounded hover:bg-gray-200 text-gray-600"
                        aria-label={r.id in expanded ? '답글 접기' : '답글 펼치기'}>
                  {r.id in expanded ? '▼' : '▶'}
                </button>
              {/if}
            </td>
            <td class="p-3 align-top">
              <div class="flex items-center gap-2">
                {#if r.author_avatar_url}
                  <img src={r.author_avatar_url} class="w-8 h-8 rounded-full" alt="" />
                {/if}
                <div>
                  <a href={`https://www.threads.net/@${r.author_username}`}
                     target="_blank" rel="noopener"
                     class="font-medium hover:text-blue-600 hover:underline"
                     title="Threads 프로필 열기">
                    @{r.author_username} ↗
                  </a>
                  {#if r.author_display_name}
                    <div class="text-xs text-gray-500">{r.author_display_name}</div>
                  {/if}
                </div>
              </div>
            </td>
            <td class="p-3 max-w-md align-top">
              <div class="truncate">{r.content}</div>
              <div class="text-xs text-gray-400">{r.post_content_preview}</div>
            </td>
            <td class="p-3 align-top text-sm">
              {#if r.replies_count > 0}
                <span class="px-2 py-0.5 bg-blue-50 text-blue-700 rounded">{r.replies_count}</span>
              {:else}
                <span class="text-gray-300">—</span>
              {/if}
            </td>
            <td class="p-3 text-sm text-gray-500 align-top">{new Date(r.created_at).toLocaleString('ko')}</td>
            <td class="p-3 align-top">
              <div class="flex flex-col gap-1">
                {#if isDmPrepared(r)}
                  <div class="flex gap-1">
                    <button onclick={() => prepareDm(r)}
                            class="flex-1 px-2 py-1 rounded text-xs font-medium bg-green-100 text-green-800 hover:bg-green-200"
                            title="메시지 다시 복사 + 프로필 다시 열기">
                      ✓ DM
                    </button>
                    <button onclick={() => undoDm(r)}
                            class="px-2 py-1 rounded text-xs text-gray-500 hover:bg-gray-100"
                            title="DM 전송 표시 취소">
                      ✕
                    </button>
                  </div>
                {:else}
                  <button onclick={() => prepareDm(r)}
                          class="px-2 py-1 rounded text-xs font-medium whitespace-nowrap bg-gray-900 text-white hover:bg-gray-700"
                          title="메시지 복사 + DM 페이지 열기">
                    💌 DM
                  </button>
                {/if}
                <div class="flex gap-2 text-xs">
                  <a href={`/admin/community/comments/${r.id}`} class="text-blue-600 hover:underline">상세</a>
                  {#if r.threads_url}
                    <a href={r.threads_url} target="_blank" rel="noopener"
                       class="text-gray-500 hover:underline">원문↗</a>
                  {/if}
                </div>
              </div>
            </td>
          </tr>
          {#if r.id in expanded}
            <tr class="bg-gray-50 border-t">
              <td></td>
              <td colspan="5" class="p-3">
                {#if expanded[r.id] === 'loading'}
                  <div class="text-sm text-gray-500">답글 불러오는 중…</div>
                {:else if expanded[r.id] === 'error'}
                  <div class="text-sm text-red-600">답글 로드 실패</div>
                {:else}
                  <ul class="space-y-2 border-l-2 border-gray-300 pl-4">
                    {#each expanded[r.id] as c (c.id)}
                      <li class="flex items-start gap-2 text-sm">
                        <a href={`https://www.threads.net/@${c.author_username}`}
                           target="_blank" rel="noopener"
                           class="font-medium text-gray-800 hover:text-blue-600 hover:underline shrink-0">
                          @{c.author_username} ↗
                        </a>
                        <span class="text-gray-700 flex-1 min-w-0">{c.content}</span>
                        <a href={`/admin/community/comments/${c.id}`}
                           class="text-xs text-blue-600 hover:underline shrink-0">상세</a>
                      </li>
                    {/each}
                    {#if expanded[r.id].length === 0}
                      <li class="text-sm text-gray-400">답글 없음</li>
                    {/if}
                  </ul>
                {/if}
              </td>
            </tr>
          {/if}
        {/each}
        {#if allReplies.length === 0}
          <tr><td colspan="6" class="p-6 text-center text-gray-500">댓글 없음</td></tr>
        {/if}
      </tbody>
    </table>
  </div>

  <!-- Infinite scroll sentinel + page indicator + manual load -->
  {#if currentPage < totalPages}
    <div bind:this={sentinel} class="py-6 text-center text-sm text-gray-500">
      {#if loadingMore}
        불러오는 중…
      {:else}
        <button onclick={loadMore} class="text-blue-600 hover:underline">
          더 보기 ({currentPage} / {totalPages})
        </button>
      {/if}
    </div>
  {:else if allReplies.length > 0}
    <p class="py-4 text-center text-xs text-gray-400">
      — 마지막 페이지 ({allReplies.length}건) —
    </p>
  {/if}
</div>
